import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import type { UnitOfMeasureDto } from '@/types/materials';

function buildSchema(t: (key: string) => string) {
  return z.object({
    code: z.string().min(1, t('form.validation.codeRequired')),
    name: z.string().min(1, t('form.validation.nameRequired')),
    status: z.enum(['Active', 'Inactive']),
  });
}

export type UomFormValues = z.infer<ReturnType<typeof buildSchema>>;

const DEFAULTS: UomFormValues = { code: '', name: '', status: 'Active' };

interface UomFormDialogProps {
  open: boolean;
  uom: UnitOfMeasureDto | null;
  onClose: () => void;
  onSubmit: (values: UomFormValues) => void;
  loading?: boolean;
}

export default function UomFormDialog({ open, uom, onClose, onSubmit, loading }: UomFormDialogProps) {
  const { t } = useTranslation('uomMaster');
  const schema = buildSchema(t);

  const { control, handleSubmit, reset } = useForm<UomFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULTS,
  });

  useEffect(() => {
    if (open) {
      reset(uom ? { code: uom.code, name: uom.name, status: uom.status as 'Active' | 'Inactive' } : DEFAULTS);
    }
  }, [open, uom, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{uom ? `${uom.code} — ${uom.name}` : t('addUom')}</DialogTitle>
      <DialogContent>
        <GridContainer spacing={2} columns={1}>
          <GridItem xs={1}>
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('form.code')}
                  fullWidth
                  sx={{ mt: 1 }}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? t('form.codeHint')}
                />
              )}
            />
          </GridItem>
          <GridItem xs={1}>
            <Controller
              name="name"
              control={control}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label={t('form.name')}
                  fullWidth
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message ?? t('form.nameHint')}
                />
              )}
            />
          </GridItem>
          {/* Status is edit-only — a brand new unit is always created Active, matching the backend's
              CreateUnitOfMeasureRequest, which doesn't accept a status at all. */}
          {uom && (
            <GridItem xs={1}>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label={t('form.status')} fullWidth helperText={t('form.statusHint')}>
                    <MenuItem value="Active">{t('form.statusActive')}</MenuItem>
                    <MenuItem value="Inactive">{t('form.statusInactive')}</MenuItem>
                  </TextField>
                )}
              />
            </GridItem>
          )}
        </GridContainer>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>{t('discardChanges')}</Button>
        <Button variant="contained" disabled={loading} onClick={handleSubmit(onSubmit)}>
          {t('saveUom')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
